import { useEffect, useState } from "react";

import { API_URL } from "../utils/Util";
import { getFetchT } from "../utils/TypedUtil";
import { RestaurantFeatureFlag } from "../../types/model";

// Module-level cache so multiple components gating on feature flags for the
// same restaurant (e.g. the nav sidebar and a route guard) share one fetch
// instead of each hitting /feature_flags on every mount.
const flagsCache = new Map<string, ReadonlyArray<RestaurantFeatureFlag>>();
const pendingRequests = new Map<string, Promise<ReadonlyArray<RestaurantFeatureFlag>>>();

function fetchFlags(restaurantId: string): Promise<ReadonlyArray<RestaurantFeatureFlag>> {
  const cached = flagsCache.get(restaurantId);
  if (cached) {
    return Promise.resolve(cached);
  }

  const pending = pendingRequests.get(restaurantId);
  if (pending) {
    return pending;
  }

  const promise = new Promise<ReadonlyArray<RestaurantFeatureFlag>>((resolve, reject) => {
    getFetchT<{ flags: ReadonlyArray<RestaurantFeatureFlag> }>(
      `${API_URL}/v1/r/${restaurantId}/feature_flags`,
      (result) => {
        flagsCache.set(restaurantId, result.flags);
        pendingRequests.delete(restaurantId);
        resolve(result.flags);
      },
      (err) => {
        pendingRequests.delete(restaurantId);
        reject(err);
      }
    );
  });
  pendingRequests.set(restaurantId, promise);
  return promise;
}

export type UseFeatureFlagsResult = {
  flags: ReadonlyArray<RestaurantFeatureFlag>;
  loading: boolean;
  isEnabled: (flagName: string) => boolean;
};

// useFeatureFlags fetches (and caches) the feature flags for a restaurant.
// Pass `undefined` when the restaurant isn't known yet (e.g. auth still
// loading); the hook then reports loading=false and every flag disabled.
export function useFeatureFlags(restaurantId: string | undefined): UseFeatureFlagsResult {
  const [flags, setFlags] = useState<ReadonlyArray<RestaurantFeatureFlag>>(
    restaurantId ? (flagsCache.get(restaurantId) ?? []) : []
  );
  const [loading, setLoading] = useState<boolean>(!!restaurantId && !flagsCache.has(restaurantId));

  useEffect(() => {
    if (!restaurantId) {
      setFlags([]);
      setLoading(false);
      return;
    }
    if (flagsCache.has(restaurantId)) {
      setFlags(flagsCache.get(restaurantId)!);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchFlags(restaurantId)
      .then((result) => {
        if (!cancelled) {
          setFlags(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFlags([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantId]);

  const isEnabled = (flagName: string): boolean =>
    flags.some((f) => f.flag_name === flagName && f.enabled);

  return { flags, loading, isEnabled };
}
